import { useState, useEffect, useMemo } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToServicios,
  updateEstadoPagoServicio,
  getAyudantesNombres,
  getCasasServicio,
  Servicio,
} from "../../firebase/firebaseService";
import {
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaChevronLeft,
  FaChevronRight,
  FaHome,
  FaUsers,
  FaCalendarAlt,
  FaCheck,
  FaUndo,
} from "react-icons/fa";

// ─── Helpers ────────────────────────────────────────────────────────────────
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const getQuincenaKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const q = date.getDate() <= 15 ? "1" : "2";
  return `${y}-${m}-${q}`;
};

const getQuincenaLabel = (key: string): string => {
  const [y, m, q] = key.split("-");
  return `${q === "1" ? "1–15" : "16–31"} de ${MESES[parseInt(m) - 1]} ${y}`;
};

const getPastQuincenas = (n: number): string[] => {
  const list: string[] = [];
  let y = new Date().getFullYear();
  let mo = new Date().getMonth() + 1;
  let q = new Date().getDate() <= 15 ? 1 : 2;
  for (let i = 0; i < n; i++) {
    if (q === 1) { q = 2; mo--; if (mo === 0) { mo = 12; y--; } }
    else q = 1;
    list.push(`${y}-${String(mo).padStart(2, "0")}-${q}`);
  }
  return list;
};

const fmt = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

const estadoPago = (sv: Servicio): "pagado" | "pendiente" =>
  (sv as any).estadoPago === "pagado" ? "pagado" : "pendiente";

// ─── Component ──────────────────────────────────────────────────────────────
export default function PagosPendientesAdmin() {
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [updating,  setUpdating]  = useState<string | null>(null); // id being updated

  const currentQ = getQuincenaKey(new Date());
  const allTabs  = [currentQ, ...getPastQuincenas(6)]; // 8 quincenas (~4 meses)
  const [activeTab, setActiveTab] = useState(currentQ);

  useEffect(() => {
    const unsub = subscribeToServicios((d) => { setServicios(d); setLoading(false); });
    return () => unsub();
  }, []);

  // ── Stats globales
  const now = new Date();
  const mesServicios = servicios.filter(
    (s) => s.fechaServicio.getFullYear() === now.getFullYear() && s.fechaServicio.getMonth() === now.getMonth()
  );
  const pendientesMes = mesServicios.filter((s) => estadoPago(s) === "pendiente");
  const pagadosMes    = mesServicios.filter((s) => estadoPago(s) === "pagado");
  const montoPendiente = pendientesMes.reduce((sum, s) => sum + (s.precioServicio || 0), 0);
  const montoCobrado   = pagadosMes.reduce((sum, s) => sum + (s.precioServicio || 0), 0);

  // ── Servicios del tab activo
  const tabServicios = useMemo(
    () => servicios.filter((s) => s.quincena === activeTab),
    [servicios, activeTab]
  );

  const tabPendientes = tabServicios.filter((s) => estadoPago(s) === "pendiente");
  const tabPagados    = tabServicios.filter((s) => estadoPago(s) === "pagado");
  const tabMontoPend  = tabPendientes.reduce((sum, s) => sum + (s.precioServicio || 0), 0);
  const tabMontoPag   = tabPagados.reduce((sum, s) => sum + (s.precioServicio || 0), 0);
  const tabTotal      = tabServicios.reduce((sum, s) => sum + (s.precioServicio || 0), 0);

  // ── Handlers
  const toggleEstado = async (sv: Servicio) => {
    if (!sv.id) return;
    setUpdating(sv.id);
    const nuevoEstado = estadoPago(sv) === "pagado" ? "pendiente" : "pagado";
    try { await updateEstadoPagoServicio(sv.id, nuevoEstado); }
    finally { setUpdating(null); }
  };

  const marcarTodosPagados = async () => {
    const pendientes = tabServicios.filter((s) => estadoPago(s) === "pendiente" && s.id);
    for (const sv of pendientes) {
      await updateEstadoPagoServicio(sv.id!, "pagado");
    }
  };

  // ── Prev/next tab nav helpers
  const tabIdx      = allTabs.indexOf(activeTab);
  const canGoPrev   = tabIdx < allTabs.length - 1;
  const canGoNext   = tabIdx > 0;

  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />

      <div className="flex-1 overflow-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-500 px-8 py-8">
          <h1 className="text-3xl font-bold text-white">Pagos Pendientes</h1>
          <p className="text-green-100 text-sm mt-1">
            Control de cobros a clientes · servicios de la empresa
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-400/40 rounded-xl flex items-center justify-center">
                <FaClock className="text-white" size={17} />
              </div>
              <div>
                <p className="text-green-100 text-[13px] font-medium">Pendiente de cobro (mes)</p>
                <p className="text-white text-2xl font-bold">${montoPendiente.toFixed(0)}</p>
                <p className="text-green-200 text-[12px]">{pendientesMes.length} servicio{pendientesMes.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-400/40 rounded-xl flex items-center justify-center">
                <FaCheckCircle className="text-white" size={17} />
              </div>
              <div>
                <p className="text-green-100 text-[13px] font-medium">Cobrado este mes</p>
                <p className="text-white text-2xl font-bold">${montoCobrado.toFixed(0)}</p>
                <p className="text-green-200 text-[12px]">{pagadosMes.length} servicio{pagadosMes.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                <FaDollarSign className="text-white" size={17} />
              </div>
              <div>
                <p className="text-green-100 text-[13px] font-medium">Total facturado mes</p>
                <p className="text-white text-2xl font-bold">
                  ${(montoPendiente + montoCobrado).toFixed(0)}
                </p>
                <p className="text-green-200 text-[12px]">{mesServicios.length} servicios totales</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-8">

          {/* Tabs de quincena */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Tab bar */}
            <div className="flex items-center border-b border-gray-100 bg-gray-50">
              <button
                onClick={() => canGoPrev && setActiveTab(allTabs[tabIdx + 1])}
                disabled={!canGoPrev}
                className="p-4 text-gray-400 hover:text-gray-600 disabled:opacity-30 shrink-0"
              >
                <FaChevronLeft size={13} />
              </button>

              <div className="flex flex-1 overflow-x-auto">
                {allTabs.map((tab, idx) => {
                  const count = servicios.filter((s) => s.quincena === tab).length;
                  const pend  = servicios.filter((s) => s.quincena === tab && estadoPago(s) === "pendiente").length;
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`relative px-4 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors shrink-0 ${
                        activeTab === tab
                          ? "bg-white text-green-700 border-b-2 border-green-500"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {idx === 0 ? "Quincena Actual" : getQuincenaLabel(tab)}
                      <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === tab ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"
                      }`}>{count}</span>
                      {pend > 0 && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400" />
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => canGoNext && setActiveTab(allTabs[tabIdx - 1])}
                disabled={!canGoNext}
                className="p-4 text-gray-400 hover:text-gray-600 disabled:opacity-30 shrink-0"
              >
                <FaChevronRight size={13} />
              </button>
            </div>

            {/* Resumen de la quincena activa */}
            {tabServicios.length > 0 && (
              <div className="flex items-center justify-between px-6 py-3 bg-gray-50/80 border-b border-gray-100 flex-wrap gap-3">
                <div className="flex items-center gap-5 text-[13px]">
                  <span className="text-gray-500">
                    <span className="font-bold text-gray-700">{tabServicios.length}</span> servicios
                  </span>
                 
                  
                </div>

                {tabPendientes.length > 0 && (
                  <button
                    onClick={marcarTodosPagados}
                    className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-green-500 text-white text-[13px] font-bold hover:bg-green-600 transition-colors shadow-md shadow-green-200"
                  >
                    <FaCheck size={11} /> Marcar todos como cobrados
                  </button>
                )}
              </div>
            )}

            {/* Tabla */}
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full" />
              </div>
            ) : tabServicios.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <FaCalendarAlt size={38} className="mb-3 text-gray-200" />
                <p className="font-medium text-[15px]">Sin servicios en esta quincena</p>
                <p className="text-[13px] mt-1">Registra servicios en la sección "Pagos Empresa"</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Casa / Cliente</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Empleado</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Horas</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Precio</th>
                      <th className="text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-6 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {tabServicios.map((sv) => {
                      const pagado  = estadoPago(sv) === "pagado";
                      const busy    = updating === sv.id;
                      return (
                        <tr
                          key={sv.id}
                          className={`transition-colors ${pagado ? "hover:bg-green-50/30" : "bg-amber-50/30 hover:bg-amber-50/60"}`}
                        >
                          <td className="px-6 py-4 text-[15px] text-gray-600">{fmt(sv.fechaServicio)}</td>

                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {getCasasServicio(sv).map((c, i) => (
                                <span key={i} className="flex items-center gap-1 text-[13px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                                  🏠 {c.nombre}
                                </span>
                              ))}
                            </div>
                          </td>

                          <td className="px-6 py-4 hidden md:table-cell">
                            <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
                              <FaUsers size={11} className="text-gray-400 shrink-0" />
                              <span>{sv.empleadoPrincipalNombre}</span>
                              {getAyudantesNombres(sv).map((n, i) => (
                                <span key={i} className="text-gray-400">+ {n}</span>
                              ))}
                            </div>
                          </td>

                          <td className="px-6 py-4 hidden sm:table-cell">
                            <span className="flex items-center gap-1 text-[15px] font-semibold text-gray-700">
                              <FaClock size={11} className="text-gray-400" />
                              {sv.horasTrabajadas}h
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            {sv.precioServicio
                              ? <span className="text-[15px] font-bold text-gray-800">${sv.precioServicio.toFixed(2)}</span>
                              : <span className="text-gray-300 text-[15px]">—</span>}
                          </td>

                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-bold ${
                              pagado
                                ? "bg-green-100 text-green-700"
                                : "bg-amber-100 text-amber-700"
                            }`}>
                              {pagado
                                ? <><FaCheckCircle size={10} /> Cobrado</>
                                : <><FaClock size={10} /> Pendiente</>}
                            </span>
                          </td>

                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => toggleEstado(sv)}
                              disabled={busy}
                              title={pagado ? "Marcar como pendiente" : "Marcar como cobrado"}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-bold transition-all disabled:opacity-50 ${
                                pagado
                                  ? "bg-gray-100 text-gray-500 hover:bg-amber-100 hover:text-amber-700"
                                  : "bg-green-100 text-green-700 hover:bg-green-200"
                              }`}
                            >
                              {busy ? (
                                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : pagado ? (
                                <><FaUndo size={10} /> Deshacer</>
                              ) : (
                                <><FaCheck size={10} /> Cobrar</>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Fila total */}
                  {tabTotal > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50">
                        <td colSpan={4} className="px-6 py-4 text-[13px] font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                          Total quincena
                        </td>
                        <td colSpan={4} className="px-6 py-4 sm:hidden text-[13px] font-bold text-gray-500 uppercase tracking-wider">
                          Total quincena
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[17px] font-extrabold text-gray-800">${tabTotal.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-[12px] font-semibold text-green-600">✓ ${tabMontoPag.toFixed(0)} cobrado</span>
                            <span className="text-[12px] font-semibold text-amber-600">⏳ ${tabMontoPend.toFixed(0)} pendiente</span>
                          </div>
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
